import * as React from 'react';
import './fields.scss';
import { useTranslations } from '../../../store/translations/hooks';
import { getWeekDayTranslationKey, nextWorkingDay, parseTimeStringToMinutes, minutesToTimeString } from '../../../shared/utils';
import { IconButton, Box } from '@mui/material';
import Add from '@material-ui/icons/Add';
import Remove from '@material-ui/icons/Remove';
import { TK } from '../../../store/translations/translationKeys';
import { useCallback } from 'react';
import { DateField, TimeField } from '.';

export interface TimeDayInterval {
    date: string,
    start: string,
    end: string,
}

export interface TimeDayIntervalFieldProp {
    id?: string,
    values: TimeDayInterval[],
    onChange: (values: TimeDayInterval[]) => void,
    onSelected?: (interval: TimeDayInterval) => void,
    disabled?: boolean,
    mandatory?: boolean,
    allowAdd?: boolean,
}

export const TimeDayIntervalField: React.FC<TimeDayIntervalFieldProp> = (props) => {
    const {
        id,
        values,
        onChange,
        onSelected,
        disabled,
        mandatory,
        allowAdd,
    } = props;

    const t = useTranslations();

    const onAddNew = () => {
        const latestValue = values[values.length-1];
        onChange(values.concat({
            date: nextWorkingDay(latestValue.date),
            start: latestValue?.start || '08:00',
            end: latestValue?.end || '09:00',
        }));
    }

    const onChangeItem = (idx: number, newVal: TimeDayInterval) => {
        onSelected?.(newVal);
        onChange(values.map((v, i) => idx === i ? newVal : v));
    }

    const onTimeChanged = (idx: number, interval: TimeDayInterval, start: string, end: string) => {
        const startMinutes = parseTimeStringToMinutes(start);
        const endMinutes = parseTimeStringToMinutes(end);

        if(startMinutes < endMinutes) onChangeItem(idx, {...interval, start, end});
        else if(end === interval.end) onChangeItem(idx, {...interval, start, end: minutesToTimeString(startMinutes+60)});
        else onChangeItem(idx, {...interval, end});
    }

    const onStartChanged = (idx: number, interval: TimeDayInterval, start: string) => {
        const duration = minutesToTimeString(parseTimeStringToMinutes(interval.end) - parseTimeStringToMinutes(interval.start))
        const newEnd = parseTimeStringToMinutes(start) + parseTimeStringToMinutes(duration)
        onTimeChanged(idx, interval, start, minutesToTimeString(newEnd))
    }
    
    const onEndChanged = (idx: number, interval: TimeDayInterval, end: string) => onTimeChanged(idx, interval, interval.start, end)

    const onDurationChanced = useCallback((idx: number, interval: TimeDayInterval) => (duration: string) => {
        const end = parseTimeStringToMinutes(interval.start) + parseTimeStringToMinutes(duration)
        onChangeItem(idx, {...interval, end: minutesToTimeString(end)})
    }, [onChangeItem])

    const isMandatoryAndOnlyHave1Value = mandatory && values.length === 1;

    return (
        <div>
            {
                values.map((interval, idx) => (
                    <div key={idx} onFocus={() => onSelected?.(interval)}>
                        <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                            <Box flex="1" minWidth="150px">
                                <DateField
                                    id={id ? `${id}-${idx}-date` : undefined}
                                    disabled={disabled}
                                    label={t(getWeekDayTranslationKey(new Date(interval.date).getDay()))}
                                    value={interval.date}
                                    onChange={date => onChangeItem(idx, { ...interval, date })}
                                />
                            </Box>
                            <Box flex="1" minWidth="100px">
                                <TimeField
                                    id={id ? `${id}-${idx}-start` : undefined}
                                    label={t(TK.start)}
                                    disabled={disabled}
                                    value={interval.start}
                                    onChange={start => onStartChanged(idx, interval, start)}
                                />
                            </Box>
                            <Box flex="1" minWidth="100px">
                                <TimeField
                                    id={id ? `${id}-${idx}-end` : undefined}
                                    label={t(TK.end)}
                                    disabled={disabled}
                                    value={interval.end}
                                    onChange={end => onEndChanged(idx, interval, end)}
                                />
                            </Box>
                            <Box flex="1" minWidth="100px">
                                <TimeField
                                    id={id ? `${id}-${idx}-duration` : undefined}
                                    label={t(TK.duration)}
                                    disabled={disabled}
                                    value={minutesToTimeString(parseTimeStringToMinutes(interval.end) - parseTimeStringToMinutes(interval.start))}
                                    onChange={onDurationChanced(idx, interval)}
                                />
                            </Box>
                            <Box display="flex" alignItems="flex-end">
                                { !disabled && !isMandatoryAndOnlyHave1Value &&
                                    <IconButton size='small' id={id ? `${id}-${idx}-remove-button` : undefined} style={{marginBottom: '4px'}} onClick={() => onChange(values.filter((_, i) => i !== idx))}>
                                        <Remove/>
                                    </IconButton>
                                }
                                { !disabled && allowAdd && isMandatoryAndOnlyHave1Value &&
                                    <IconButton size='small' id={id ? `${id}-${idx}-add-button` : undefined} style={{marginBottom: '4px'}} onClick={onAddNew}><Add/></IconButton>
                                }
                            </Box>
                        </Box>
                    </div>
                ))
            }
            { !disabled && allowAdd && !isMandatoryAndOnlyHave1Value && 
                <div style={{display: 'flex', justifyContent: 'end'}}>
                    <IconButton size='small' id={id ? `${id}-add-button` : undefined} style={{marginTop: 'var(--spacer-XS)'}} onClick={onAddNew}>
                        <Add/>
                    </IconButton> 
                </div>
            }
        </div>
    );
}